import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2, CheckCircle, Circle, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import ConfirmDialog from '../../Dialogs/ConfirmDialog';
import { useAccessTheme } from './useAccessTheme';

const ASSIGNED_PAGE_SIZE = 8;
const PICKER_PAGE_SIZE = 8;

interface User {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    isActive: boolean;
}

interface AssignUsersModalProps {
    roleName: string;
    assignedUsers: User[];
    availableUsers: User[];
    onClose: () => void;
    onAssign: (userId: string) => void;
    onRemove: (userId: string) => void;
}

const AssignUsersModal: React.FC<AssignUsersModalProps> = ({
    roleName,
    assignedUsers,
    availableUsers,
    onClose,
    onAssign,
    onRemove
}) => {
    const t = useAccessTheme();
    const [selectedUserId, setSelectedUserId] = useState('');
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [assignedPage, setAssignedPage] = useState(1);
    const [pickerPage, setPickerPage] = useState(1);

    const unassignedUsers = availableUsers.filter(
        (u) => !assignedUsers.find((au) => au.id === u.id)
    );

    const filteredUsers = unassignedUsers.filter((user) =>
        `${user.firstName} ${user.lastName} ${user.email}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
    );
    const getFullName = (u: User) => `${u.firstName} ${u.lastName}`;
    const selectedUser = unassignedUsers.find((u) => u.id === selectedUserId);

    const assignedPageCount = Math.max(1, Math.ceil(assignedUsers.length / ASSIGNED_PAGE_SIZE));
    const pickerPageCount = Math.max(1, Math.ceil(filteredUsers.length / PICKER_PAGE_SIZE));
    const pagedAssignedUsers = assignedUsers.slice(
        (assignedPage - 1) * ASSIGNED_PAGE_SIZE,
        assignedPage * ASSIGNED_PAGE_SIZE
    );
    const pagedPickerUsers = filteredUsers.slice(
        (pickerPage - 1) * PICKER_PAGE_SIZE,
        pickerPage * PICKER_PAGE_SIZE
    );

    useEffect(() => {
        setAssignedPage((page) => Math.min(page, assignedPageCount));
    }, [assignedPageCount]);

    useEffect(() => {
        setPickerPage(1);
    }, [searchTerm]);

    useEffect(() => {
        setPickerPage((page) => Math.min(page, pickerPageCount));
    }, [pickerPageCount]);

    return createPortal(
        <div className={`fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 ${t.overlay}`}>
            <div className={`rounded-xl shadow-xl w-full max-w-2xl max-h-[min(720px,90vh)] overflow-hidden border flex flex-col ${t.panel}`}>
                <div className={`flex items-center justify-between px-5 py-4 border-b ${t.header}`}>
                    <div>
                        <h3 className={`text-base font-semibold ${t.title}`}>Assign Users</h3>
                        <p className={`text-xs mt-0.5 ${t.muted}`}>
                            Role: <span className={t.accentText}>{roleName}</span>
                        </p>
                    </div>
                    <button type="button" onClick={onClose} className={`p-1.5 rounded-lg ${t.closeBtn}`}>
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 overflow-y-auto min-h-0 flex-1">
                    <div className="flex gap-2 items-center mb-5">
                        <div className="relative flex-1">
                            <button
                                type="button"
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                className={`w-full border rounded-lg px-3 py-2 text-sm flex items-center justify-between text-left ${t.input}`}
                            >
                                <span className={`truncate ${selectedUser ? t.title : t.muted}`}>
                                    {selectedUser
                                        ? `${getFullName(selectedUser)} — ${selectedUser.email}`
                                        : 'Select a user to assign...'}
                                </span>
                                <ChevronDown className={`w-4 h-4 shrink-0 ${t.muted}`} />
                            </button>

                            {dropdownOpen && (
                                <div className={`absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border shadow-lg ${t.panel}`}>
                                    <div className={`sticky top-0 p-2 border-b ${t.border} ${t.isLight ? 'bg-white' : 'bg-neutral-900'}`}>
                                        <input
                                            autoFocus
                                            type="text"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            placeholder="Search..."
                                            className={`w-full border rounded-md px-3 py-1.5 text-sm outline-none ${t.input}`}
                                        />
                                    </div>
                                    {pagedPickerUsers.length > 0 ? (
                                        pagedPickerUsers.map((user) => (
                                            <button
                                                type="button"
                                                key={user.id}
                                                onClick={() => {
                                                    setSelectedUserId(user.id);
                                                    setDropdownOpen(false);
                                                    setSearchTerm('');
                                                }}
                                                className={`w-full text-left px-3 py-2 text-sm ${t.rowHover}`}
                                            >
                                                <div className={`font-medium ${t.title}`}>{getFullName(user)}</div>
                                                <div className={`text-xs ${t.muted}`}>{user.email}</div>
                                            </button>
                                        ))
                                    ) : (
                                        <div className={`px-3 py-3 text-xs text-center ${t.muted}`}>No matching users.</div>
                                    )}
                                    {filteredUsers.length > PICKER_PAGE_SIZE && (
                                        <div className={`sticky bottom-0 flex items-center justify-between gap-2 px-3 py-2 border-t text-xs ${t.border} ${t.isLight ? 'bg-white' : 'bg-neutral-900'} ${t.muted}`}>
                                            <span>
                                                {(pickerPage - 1) * PICKER_PAGE_SIZE + 1}-{Math.min(pickerPage * PICKER_PAGE_SIZE, filteredUsers.length)} of {filteredUsers.length}
                                            </span>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    type="button"
                                                    disabled={pickerPage <= 1}
                                                    onClick={() => setPickerPage((page) => Math.max(1, page - 1))}
                                                    className={`p-1 rounded disabled:opacity-40 ${t.actionHover}`}
                                                >
                                                    <ChevronLeft className="w-4 h-4" />
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={pickerPage >= pickerPageCount}
                                                    onClick={() => setPickerPage((page) => Math.min(pickerPageCount, page + 1))}
                                                    className={`p-1 rounded disabled:opacity-40 ${t.actionHover}`}
                                                >
                                                    <ChevronRight className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <button
                            type="button"
                            onClick={() => {
                                if (selectedUserId) onAssign(selectedUserId);
                                setSelectedUserId('');
                            }}
                            disabled={!selectedUserId}
                            className={`px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 ${t.btnPrimary}`}
                        >
                            Add
                        </button>
                    </div>

                    <div className={`overflow-hidden border rounded-lg ${t.tableShell}`}>
                        <div className="overflow-x-auto max-h-[36vh]">
                        <table className="w-full text-left text-sm">
                            <thead className={`${t.tableHead} text-xs uppercase tracking-wide`}>
                                <tr>
                                    <th className="px-4 py-2.5">Name</th>
                                    <th className="px-4 py-2.5">Email</th>
                                    <th className="px-4 py-2.5">Status</th>
                                    <th className="px-4 py-2.5 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedAssignedUsers.map(user => (
                                    <tr key={user.id} className={`border-t ${t.rowBorder} ${t.rowHover}`}>
                                        <td className={`px-4 py-2.5 font-medium ${t.title}`}>{getFullName(user)}</td>
                                        <td className={`px-4 py-2.5 ${t.body}`}>{user.email}</td>
                                        <td className="px-4 py-2.5">
                                            {user.isActive ? (
                                                <span className="flex items-center gap-1 text-green-600 text-xs font-medium">
                                                    <CheckCircle className="w-3.5 h-3.5" /> Active
                                                </span>
                                            ) : (
                                                <span className={`flex items-center gap-1 text-xs font-medium ${t.muted}`}>
                                                    <Circle className="w-3.5 h-3.5" /> Inactive
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5 text-right">
                                            <button
                                                type="button"
                                                onClick={() => setConfirmDeleteId(user.id)}
                                                className={`p-1.5 rounded ${t.muted} ${t.actionHover} hover:text-red-500`}
                                                title="Unassign"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {assignedUsers.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className={`text-center py-8 text-xs ${t.muted}`}>
                                            No users assigned to this role.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        </div>
                        {assignedUsers.length > ASSIGNED_PAGE_SIZE && (
                            <div className={`flex items-center justify-between gap-2 px-4 py-2.5 border-t text-xs ${t.border} ${t.muted}`}>
                                <span>
                                    {(assignedPage - 1) * ASSIGNED_PAGE_SIZE + 1}-{Math.min(assignedPage * ASSIGNED_PAGE_SIZE, assignedUsers.length)} of {assignedUsers.length}
                                </span>
                                <div className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        disabled={assignedPage <= 1}
                                        onClick={() => setAssignedPage((page) => Math.max(1, page - 1))}
                                        className={`p-1 rounded disabled:opacity-40 ${t.actionHover}`}
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                    </button>
                                    <span className={t.body}>Page {assignedPage} of {assignedPageCount}</span>
                                    <button
                                        type="button"
                                        disabled={assignedPage >= assignedPageCount}
                                        onClick={() => setAssignedPage((page) => Math.min(assignedPageCount, page + 1))}
                                        className={`p-1 rounded disabled:opacity-40 ${t.actionHover}`}
                                    >
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className={`px-5 py-4 border-t flex justify-end ${t.footer}`}>
                    <button type="button" onClick={onClose} className={`px-4 py-2 rounded-lg text-sm font-medium ${t.btnSecondary}`}>
                        Close
                    </button>
                </div>

                {confirmDeleteId && (
                    <ConfirmDialog
                        message="Are you sure you want to unassign this user from the role?"
                        onYes={() => {
                            onRemove(confirmDeleteId);
                            setConfirmDeleteId(null);
                        }}
                        onCancel={() => setConfirmDeleteId(null)}
                    />
                )}
            </div>
        </div>,
        document.body
    );
};

export default AssignUsersModal;
